import { FC, useMemo, useState } from 'react';
import { ChevronRight, FolderOpen, Search } from 'lucide-react';
import { ParcImmobilierMinimalDto } from 'api/real-estate';
import { formatEuroPrice } from 'features/subscription-process/home-insurance/utils/realEstateFormatters';

interface ParcsListProps {
  parcs: ParcImmobilierMinimalDto[];
  onSelectParc: (parc: ParcImmobilierMinimalDto) => void;
}

/**
 * Sous-onglet "Parcs" : recherche parmi les parcs du client (filtre client — volumétrie
 * faible) et cartes cliquables vers le détail d'un parc.
 */
export const ParcsList: FC<ParcsListProps> = ({ parcs, onSelectParc }) => {
  const [search, setSearch] = useState('');

  const filteredParcs = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return parcs;
    return parcs.filter(
      parc =>
        (parc.name ?? '').toLowerCase().includes(query) ||
        (parc.reference ?? '').toLowerCase().includes(query)
    );
  }, [parcs, search]);

  return (
    <div className="space-y-4">
      {/* Recherche */}
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un parc par nom ou référence…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
      </div>

      {/* Cartes parcs */}
      {filteredParcs.length === 0 ? (
        <div className="bg-white rounded-xl p-8 shadow-sm border text-center">
          <p className="text-sm text-gray-500">Aucun parc ne correspond à la recherche.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filteredParcs.map(parc => (
            <button
              key={parc.id}
              type="button"
              onClick={() => onSelectParc(parc)}
              className="group text-left bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md hover:border-primary/30 transition-all duration-300"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <FolderOpen className="w-4 h-4 text-primary" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 truncate">{parc.name}</h4>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                    {parc.reference}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="bg-gray-50 rounded-lg py-2 px-1 text-center">
                  <div className="text-sm font-bold text-gray-800">{parc.realEstatesCount}</div>
                  <div className="text-[11px] text-gray-500">
                    {parc.realEstatesCount > 1 ? 'Biens' : 'Bien'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg py-2 px-1 text-center">
                  <div className="text-sm font-bold text-gray-800">
                    {parc.totalArea != null ? `${parc.totalArea} m²` : '—'}
                  </div>
                  <div className="text-[11px] text-gray-500">Surface</div>
                </div>
                <div className="bg-gray-50 rounded-lg py-2 px-1 text-center">
                  <div className="text-sm font-bold text-gray-800 truncate">
                    {parc.totalRent != null ? formatEuroPrice(parc.totalRent) : '—'}
                  </div>
                  <div className="text-[11px] text-gray-500">Loyers/mois</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
